import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';

import { Stack } from 'ui/stack';

import Button from 'components/partials/button/button';
import IconButton from 'components/partials/button/icon-button';
import type { ButtonVariant } from 'components/partials/button/button';

import CohortCampus from 'containers/admin/clients/client/tabs-components/critical-populations/components/cohort-campus';
import DecisionPanel from 'containers/admin/clients/client/tabs-components/critical-populations/components/decision-panel';
import DeferredReasonBox from 'containers/admin/clients/client/tabs-components/critical-populations/components/deferred-reason-box';
import MonitoringAlert from 'containers/admin/clients/client/tabs-components/critical-populations/components/monitoring-alert';
import OriginBadge from 'containers/admin/clients/client/tabs-components/critical-populations/components/origin-badge';
import {
  CARD_ACTIONS_BY_TAB,
  CARD_ACTION_LABELS,
} from 'containers/admin/clients/client/tabs-components/critical-populations/critical-populations.constants';
import {
  formatDimensionSubtitle,
  formatDisplayDate,
} from 'containers/admin/clients/client/tabs-components/critical-populations/critical-populations.helpers';
import type {
  CaptureListTab,
  DecisionActionKey,
  DeleteTarget,
} from 'containers/admin/clients/client/tabs-components/critical-populations/critical-populations.types';

import type { CriticalPopulationResponse } from 'api/generated/schemas';

import Icons from 'assets/icons';

type CardAction = (typeof CARD_ACTIONS_BY_TAB)[CaptureListTab][number];

interface ActionPresentation {
  variant: ButtonVariant;
  icon: ReactNode;
}

const ACTION_PRESENTATION: Record<CardAction, ActionPresentation> = {
  edit: {
    variant: 'brand-outlined',
    icon: <Icons.EditPencil className="size-4" aria-hidden="true" />,
  },
  'promote-evaluating': {
    variant: 'brand-filled',
    icon: <Icons.CheckCircle className="size-4" aria-hidden="true" />,
  },
  'defer-evaluating': {
    variant: 'neutral-outlined',
    icon: <Icons.History className="size-4" aria-hidden="true" />,
  },
  'defer-active': {
    variant: 'neutral-outlined',
    icon: <Icons.History className="size-4" aria-hidden="true" />,
  },
  'promote-deferred': {
    variant: 'brand-filled',
    icon: <Icons.CheckCircle className="size-4" aria-hidden="true" />,
  },
};

interface CaptureCardProps {
  row: CriticalPopulationResponse;
  tab: CaptureListTab;
  clientId: number;
  showMonitoringAlert?: boolean;
  openAction: DecisionActionKey | null;
  onEdit: (id: number) => void;
  onDelete: (target: DeleteTarget) => void;
  onOpenDecision: (id: number, action: DecisionActionKey) => void;
  onCloseDecision: () => void;
  onDecisionDone: () => void;
}

const CaptureCard = ({
  row,
  tab,
  clientId,
  showMonitoringAlert = false,
  openAction,
  onEdit,
  onDelete,
  onOpenDecision,
  onCloseDecision,
  onDecisionDone,
}: CaptureCardProps) => {
  const { id } = row;
  const actionRefs = useRef<Partial<Record<string, HTMLButtonElement | null>>>(
    {}
  );
  const actionToRestore = useRef<string | null>(null);

  useEffect(() => {
    if (!openAction && actionToRestore.current) {
      actionRefs.current[actionToRestore.current]?.focus();
      actionToRestore.current = null;
    }
  }, [openAction]);

  if (id === undefined) return null;

  const handleCancel = (): void => {
    actionToRestore.current = openAction;
    onCloseDecision();
  };

  const handleAction = (action: CardAction): void => {
    if (action === 'edit') onEdit(id);
    else onOpenDecision(id, action);
  };

  const isRecommended = row.origin === 'RECOMMENDED';
  const hasMeta = tab !== 'deferred' || !!row.cohort || !!row.campus;

  return (
    <li
      className="overflow-hidden rounded-[10px] border border-navy-100 bg-white"
      data-testid={`capture-card-${id}`}
    >
      <Stack sx={{ gap: 1, px: 2, pb: 1.5, pt: 2 }}>
        <Stack direction="row">
          <OriginBadge origin={row.origin} />
        </Stack>
        <Stack sx={{ gap: 0.5 }}>
          <h3 className="text-base font-semibold text-gray-700">
            {row.summary}
          </h3>
          <p className="text-xs font-medium text-gray-500">
            {formatDimensionSubtitle(row.dimensions)}
          </p>
        </Stack>
        {hasMeta && (
          <Stack
            direction="row"
            sx={{
              flexWrap: 'wrap',
              columnGap: 1.5,
              rowGap: 0.5,
              pt: 0.5,
              fontSize: '0.75rem',
              lineHeight: '1rem',
              fontWeight: 500,
              color: 'tokens.gray.500',
            }}
          >
            <CohortCampus cohort={row.cohort} campus={row.campus} />
            {tab !== 'deferred' &&
              (isRecommended ? (
                <span>Generated: {formatDisplayDate(row.createdDate)}</span>
              ) : (
                <>
                  <span>Added by {row.createdBy}</span>
                  <span>Updated {formatDisplayDate(row.lastModifiedDate)}</span>
                </>
              ))}
          </Stack>
        )}
        {tab === 'active' && showMonitoringAlert && (
          <MonitoringAlert
            data-testid={`capture-card-${id}-monitoring-alert`}
          />
        )}
        {tab === 'deferred' && row.note && (
          <DeferredReasonBox
            note={row.note}
            deferredDate={row.lastModifiedDate}
          />
        )}
      </Stack>
      {openAction ? (
        <DecisionPanel
          clientId={clientId}
          id={id}
          action={openAction}
          onCancel={handleCancel}
          onDone={onDecisionDone}
        />
      ) : (
        <Stack
          direction="row"
          sx={{
            alignItems: 'center',
            gap: 1.5,
            borderTopWidth: '1px',
            borderColor: 'tokens.navy.100',
            px: 2,
            pb: 2,
            pt: 1.5,
          }}
        >
          {CARD_ACTIONS_BY_TAB[tab].map((action) => (
            <Button
              key={action}
              ref={(node) => {
                actionRefs.current[action] = node;
              }}
              variant={ACTION_PRESENTATION[action].variant}
              size="sm"
              iconBefore={ACTION_PRESENTATION[action].icon}
              onClick={() => handleAction(action)}
              data-testid={`capture-card-${id}-${action}`}
            >
              {CARD_ACTION_LABELS[action]}
            </Button>
          ))}
          <IconButton
            variant="critical-outlined"
            size="sm"
            icon={<Icons.Delete aria-hidden="true" />}
            aria-label={`Delete ${row.summary}`}
            className="ml-auto"
            onClick={() => onDelete({ id, summary: row.summary ?? '' })}
            data-testid={`capture-card-${id}-delete`}
          />
        </Stack>
      )}
    </li>
  );
};

export default CaptureCard;
